import {Section} from 'common/components/items-menu/types';
import {ProjectFeatureFlag, REPORT_BASICS_LINKS, ReportName, UNIQUE_PROJECTS} from 'common/report/constants';

import {HelpOnboardingSectionItemValue, NavReportConfig} from './types';

// make sure these values are in sync with common.styl's $chrome-header-height(s)
export const chromeHeaderPrimaryHeight = 60;
const chromeHeaderSecondaryHeight = 50;
export const chromeHeaderHeight = chromeHeaderPrimaryHeight + chromeHeaderSecondaryHeight;
export const DARK_MODE_KEY = `darkModeOn`;
export const LAST_USER_PROJECT_KEY = `lastUserProjectId`;

// for types of credit state, see BillingCredit django model's def state()
export enum CreditState {
  Valid = `valid`,
  Redeemed = `redeemed`,
  Expired = `expired`,
  NotEffective = `not_effective`,
}

export enum HelpLabels {
  AskQuestions = `Ask Questions`,
  CommunityWidget = `Community FAQs`,
  ContactUs = `Contact Us`,
  ContactSales = `Contact Sales`,
  ContactSupport = `Contact Support`,
  DemoProject = `Mixpanel Demo Project`,
  Docs = `Documentation`,
  SubmitFeedback = `Submit Feedback`,
  GettingStarted = `Getting Started`,
  Intro = `Quick Start`,
  LiveChat = `Live Chat`,
  ProductUpdates = `What's New`,
  Community = `Visit Community`,
  HireAnExpert = `Hire an Expert`,
  YourPrivacyRights = `Your Privacy Rights`,
  OnboardingGuide = `Onboarding Guide`,
}

export enum HelpTrackingEvent {
  CommunityWidget = `[Help Widget] Community Widget`,
  ContactSales = `[Help Widget] Contact Sales`,
  Docs = `[Help Widget] Documentation`,
  ExternalLink = `[Help Widget] External Link`,
  Intro = `[Help Widget] Learn Mixpanel Basics`,
  SetupGuide = `[Help Widget] Setup Guide`,
  GettingStarted = `[Help Widget] Getting Started`,
  WhatsNew = `[Help Widget] What's New`,
  LiveChat = `[Help Widget] Live Chat Started`,
  Community = `[Help Widget] Visit Community`,
  HireAnExpert = `[Help Widget] Hire an Expert`,
  Support = `[Help Widget] Support`,
  OnboardingGuide = `[Help Widget] Onboarding Guide`,
}

export enum HelpGettingStartedLabels {
  SetupGuide = `Setup Guide`,
  Dashboards = `Boards`,
  Insights = `Insights`,
  Funnels = `Funnels`,
  Flows = `Flows`,
  Retention = `Retention`,
}

export enum HelpSectionItemType {
  GettingStarted = `GettingStarted`,
  ProductUpdate = `ProductUpdate`,
}

export const CHAMELEON_TOUR_QUERY_PARAM = `chmlnTour`;

export const CONTACT_US_SECTIONS = [];

export const HELP_GETTING_STARTED_SECTIONS: Section<HelpOnboardingSectionItemValue> = {
  items: [
    {
      label: HelpLabels.Intro,
      icon: `brand-mixpanel`,
    },
    {
      label: HelpGettingStartedLabels.SetupGuide,
      icon: `setup-guide`,
    },
    {
      label: HelpGettingStartedLabels.Dashboards,
      icon: `brand-dashboard`,
      value: {
        helpLink: REPORT_BASICS_LINKS[ReportName.Dashboards],
        type: HelpSectionItemType.GettingStarted,
      },
    },
    {
      label: HelpGettingStartedLabels.Insights,
      icon: `brand-insights`,
      value: {
        helpLink: REPORT_BASICS_LINKS[ReportName.Insights],
        type: HelpSectionItemType.GettingStarted,
      },
    },
    {
      label: HelpGettingStartedLabels.Funnels,
      icon: `brand-funnels`,
      value: {
        helpLink: REPORT_BASICS_LINKS[ReportName.Funnels],
        type: HelpSectionItemType.GettingStarted,
      },
    },
    {
      label: HelpGettingStartedLabels.Flows,
      icon: `brand-flows`,
      value: {
        helpLink: REPORT_BASICS_LINKS[ReportName.Flows],
        type: HelpSectionItemType.GettingStarted,
      },
    },
    {
      label: HelpGettingStartedLabels.Retention,
      icon: `brand-retention`,
      value: {
        helpLink: REPORT_BASICS_LINKS[ReportName.Retention],
        type: HelpSectionItemType.GettingStarted,
      },
    },
  ],
};

export const ALL_PRODUCT_UPDATES_LINK = `https://docs.mixpanel.com/changelogs`;

export enum NavCategoryType {
  Analysis = `Analysis`,
  Applications = `Applications`,
  Dashboards = `Boards`,
  DataManagement = `Data Management`,
  Events = `Events`,
  Project = `Project`,
  Settings = `Settings`,
  Users = `Users`,
}

// remove this and fill this out in REPORT_INFO for the report if it is used in full display in the primary nav
const UNUSED_NAVIGATION_REPORT_INFO = {
  description: ``,
  icon: ``,
  label: ``,
};

export const REPORT_INFO: {[key in ReportName]?: NavReportConfig} = {
  [ReportName.Dashboards]: {
    ...UNUSED_NAVIGATION_REPORT_INFO,
    endpoint: () => `dashboards`,
    endpointAliases: [`dashboard`],
    category: NavCategoryType.Dashboards,
  },
  [ReportName.Insights]: {
    description: `Explore your data by segmenting and filtering event and user counts`,
    endpoint: () => `insights`,
    icon: `brand-insights`,
    label: `Insights`,
    category: NavCategoryType.Analysis,
  },
  [ReportName.Funnels]: {
    description: `Understand the conversion and drop-off of critical sequences by measuring user progress at each step`,
    icon: `brand-funnels`,
    label: `Funnels`,
    endpoint: () => `funnels`,
    category: NavCategoryType.Analysis,
  },
  [ReportName.Flows]: {
    description: `Analyze how users move through your product by exploring paths between key actions`,
    icon: `brand-flows`,
    label: `Flows`,
    endpoint: () => `flows`,
    category: NavCategoryType.Analysis,
  },
  [ReportName.Retention]: {
    description: `Assess stickiness of your product by identifying how many users come back over time`,
    icon: `brand-retention`,
    label: `Retention`,
    endpoint: () => `retention`,
    category: NavCategoryType.Analysis,
  },
  [ReportName.Signal]: {
    description: `Uncover insights by finding correlations between user actions`,
    icon: `brand-signal`,
    label: `Signal`,
    endpoint: () => `signal`,
    category: NavCategoryType.Applications,
  },
  [ReportName.Impact]: {
    description: `Determine the impact of a new feature by comparing behavior before and after launch`,
    icon: `brand-impact`,
    label: `Impact`,
    endpoint: () => `impact`,
    category: NavCategoryType.Applications,
  },
  [ReportName.ExperimentReporting]: {
    beta: true,
    description: `View an A/B test's effect by comparing metrics between test variants`,
    icon: `brand-experiments`,
    label: `Experiments`,
    endpoint: () => `experiment-reporting`,
    category: NavCategoryType.Applications,
    hidden: (reportContext): boolean => {
      return [
        UNIQUE_PROJECTS.Uber, // Uber
        UNIQUE_PROJECTS.UberEats, // Uber-Eats
      ].includes(reportContext.projectId?.toString());
    },
  },
  [ReportName.Users]: {
    description: `Understand each user`,
    icon: `brand-explore`,
    label: `Users`,
    endpoint: () => `users`,
    endpointAliases: [`profile`],
    category: NavCategoryType.Users,
  },
  [ReportName.Profile]: {
    ...UNUSED_NAVIGATION_REPORT_INFO,
    endpoint: () => `profile`,
    category: NavCategoryType.Users,
  },
  [ReportName.Lexicon]: {
    description: `Document and transform your events and properties`,
    icon: `brand-lexicon`,
    label: `Lexicon`,
    endpoint: () => `lexicon#transformations`,
    category: NavCategoryType.DataManagement,
  },
  [ReportName.DataAudit]: {
    description: `View possible issues with your implementation`,
    icon: `brand-audit`,
    label: `Data Audit`,
    beta: true,
    endpoint: () => `lexicon#audit`,
    hidden: (reportContext): boolean =>
      !(
        reportContext.project?.hasFeatureFlag(ProjectFeatureFlag.ShowReportDataAudit) &&
        reportContext.isProjectOrGlobalWorkspace
      ),
    category: NavCategoryType.DataManagement,
  },
  [ReportName.Events]: {
    description: `View the stream of your events and individual event details`,
    endpoint: () => `events`,
    icon: `brand-liveview`,
    label: `Events`,
    category: NavCategoryType.Events,
  },
  [ReportName.Cohorts]: {
    description: `Manage your saved cohorts`,
    icon: `brand-cohorts`,
    label: `Cohorts`,
    endpoint: (reportContext): string =>
      reportContext.project?.hasFeatureFlag(ProjectFeatureFlag.DisableEntityManagementCohorts)
        ? `cohorts`
        : `entity-management#~(entityType~'cohorts)`,
    category: NavCategoryType.DataManagement,
  },
  [ReportName.Integrations]: {
    description: `Connect Mixpanel to a data pipeline or customer engagement tool`,
    icon: `brand-integrations`,
    label: `Integrations`,
    endpoint: () => `integrations`,
    disabling: {
      isDisabled: (reportContext) => !reportContext.isProjectOrGlobalWorkspace,
      disabledMsg: `Integration settings are only available in the global Data View`,
    },
    category: NavCategoryType.DataManagement,
  },
  [ReportName.JqlConsole]: {
    label: `JQL`,
    description: `Write powerful queries to analyze your data in Mixpanel.`,
    icon: ``,
    endpoint: () => `jql-console`,
    // JQL would allow users to bypass data filters, it should be hidden for workspaces
    hidden: (reportContext): boolean => reportContext.project?.hasFeatureFlag(ProjectFeatureFlag.HideReportJqlConsole),
    disabling: {
      isDisabled: (reportContext) => !reportContext.isProjectOrGlobalWorkspace,
      disabledMsg: `JQL Console is only available in the global Data View`,
    },
    category: NavCategoryType.Applications,
  },
  [ReportName.Segmentation]: {
    label: `Segmentation`,
    description: `Measure the actions that people take.`,
    icon: ``,
    endpoint: () => `segmentation`,
    hidden: () => true,
    category: NavCategoryType.Applications,
  },
  [ReportName.Revenue]: {
    label: `Revenue`,
    description: `Easily understand and analyze the revenue you make from individual customers.`,
    icon: ``,
    endpoint: () => `revenue`,
    hidden: (reportContext): boolean =>
      reportContext.projectId > 2845000 || // hide revenue for all new projects
      reportContext.project?.hasFeatureFlag(ProjectFeatureFlag.HideReportRevenue) ||
      !reportContext.isProjectOrGlobalWorkspace,
    category: NavCategoryType.Applications,
  },
  [ReportName.UserSettings]: {
    ...UNUSED_NAVIGATION_REPORT_INFO,
    endpoint: () => `settings/account`,
    category: NavCategoryType.Settings,
  },
  [ReportName.Settings]: {
    ...UNUSED_NAVIGATION_REPORT_INFO,
    endpoint: () => `settings`,
    category: NavCategoryType.Settings,
  },
  [ReportName.Setup]: {
    ...UNUSED_NAVIGATION_REPORT_INFO,
    endpoint: () => `setup`,
    category: NavCategoryType.Settings,
  },
  [ReportName.Views]: {
    ...UNUSED_NAVIGATION_REPORT_INFO,
    endpoint: () => `views`,
    category: NavCategoryType.Project,
  },
};
